import Vue from 'vue'
import Component, { mixins } from 'vue-class-component'
import { ProductApiModel } from '@/interfaces/ProductApiModel'
import { OptionApiModel } from '@/interfaces/OptionApiModel'
import { namespace } from 'vuex-class'
import { useOptionStore } from '@/store/modules/options'
import { SubOptionType } from '@/interfaces/SubOptionType'
import { ConfirmModalData } from '@/interfaces/ConfirmModalData'
import { RequiredOptionsLogic } from '@/interfaces/RequiredOptionsLogic'
import { OptionCategoryApiModel } from '@/interfaces/OptionCategoryApiModel'
import { OptionValidator } from './OptionValidator'
const currentModel = namespace('currentModel')

@Component
export class OptionSelectionMixin extends mixins(OptionValidator) {
  public optionStore = useOptionStore();

  @currentModel.Action
  public toggleUpdating!: (isBeingUpdated: boolean) => void

  @currentModel.Action
  public updateModelProperty!: (payload: { property: string; value: any }) => void

  @currentModel.Action
  public removeModelPropertyOption!: (payload: { property: string; value: string }) => void

  @currentModel.Action
  public addModelPropertyOption!: (payload: { property: string; value: string }) => void

  public async selectOption(optionItem: OptionApiModel, forceDeselection = false): Promise<void> {
    this.toggleUpdating(true)
    await this.selectOptionInternal(optionItem, forceDeselection)
    this.toggleUpdating(false)
  }

  public async deselectOption(optionItem: OptionApiModel, forceDeselection = false,
    skipDependencies = false) {
    this.toggleUpdating(true)
    await this.deselectOptionInternal(optionItem, forceDeselection, skipDependencies)
    this.toggleUpdating(false)
  }

  private async deselectOptionInternal(optionItem: OptionApiModel,
    forceDeselection = false,
    skipDependencies = false): Promise<void> {
    const modelProperty = optionItem.optionType.modelProperty

    let fallbackOption = this.optionStore.allOptions.find(o => o.optionType.type === optionItem.optionType.type && o.isFallbackDefault)
    if (!fallbackOption && !(optionItem.optionType.multipleSelection || optionItem.removable)) {
      if (modelProperty === 'panelFinish' && optionItem.code.endsWith('F')) {
        fallbackOption = this.optionStore.allOptions.find(o => o.code === optionItem.code.replace('F', ''))
      }
      if (!fallbackOption) {
        fallbackOption = this.optionStore.allOptions.find(o => o.optionType.type === optionItem.optionType.type)
      }
    }

    if (!skipDependencies) {
      const canContinue = await this.deselectDependencies(optionItem, undefined, forceDeselection)
      if (!canContinue) { return }
    }

    if (optionItem.optionType.multipleSelection) {
      this.removeModelPropertyOption({ property: modelProperty, value: optionItem.code })
    } else {
      this.updateModelProperty({ property: modelProperty + 'Category', value: 'NONE' })
      this.updateModelProperty({ property: modelProperty + 'Frames', value: null })
      this.updateModelProperty({ property: modelProperty, value: 'NONE' })
      this.updateModelProperty({ property: modelProperty + 'Description', value: null })
    }

    if (fallbackOption) {
      if (!this.isOptionSelected(fallbackOption)) {
        await this.selectOptionInternal(fallbackOption, forceDeselection)
      }
    }
  }

  private async selectOptionInternal(optionItem: OptionApiModel, forceDeselection = false): Promise<void> {
    if (this.isOptionSelected(optionItem)) { return }
    const category = this.optionStore.allCategories.find(c => c.id === optionItem.categoryId)
    if (!category) { return }

    const modelProperty = optionItem.optionType.modelProperty

    if (!optionItem.optionType.multipleSelection) {
      const currentOptionCode = this.model.configurator[modelProperty]
      if (!!currentOptionCode && currentOptionCode !== 'NONE') {
        const currentOption = this.optionStore.allOptions.find(o => o.code === currentOptionCode) as OptionApiModel
        if (currentOption) {
          const canContinue = await this.deselectDependencies(currentOption, optionItem, forceDeselection)
          if (!canContinue) { return }
        }
      }
    }

    if (optionItem.optionType.multipleSelection) {
      this.addModelPropertyOption({ property: modelProperty, value: optionItem.code })
    } else {
      this.updateModelProperty({ property: modelProperty + 'Category', value: category.code })
      this.updateModelProperty({ property: modelProperty + 'Frames', value: optionItem.frames !== undefined ? optionItem.frames : null })
      this.updateModelProperty({ property: modelProperty, value: optionItem.code })
    }

    this.updateSubOptions(category)

    await this.selectRequiredOptions(optionItem, forceDeselection)
  }

  private async selectRequiredOptions(optionItem: OptionApiModel, forceDeselection = false) {
    if (optionItem.requiredOptions && optionItem.requiredOptions.length > 0) {
      const requiredOptions = this.optionStore.allOptions.filter(o => optionItem.requiredOptions?.includes(o.code))

      if (optionItem.requiredOptionsLogic === RequiredOptionsLogic.All) {
        for (const option of requiredOptions) {
          if (!this.isOptionSelected(option)) {
            await this.selectOptionInternal(option, forceDeselection)
          }
        }
      } else {
        const anySelected = requiredOptions.some(option => this.isOptionSelected(option))
        if (!anySelected) {
          const first = requiredOptions[0]
          if (!this.isOptionSelected(first)) {
            await this.selectOptionInternal(first, forceDeselection)
          }
        }
      }
    }

    return Promise.resolve()
  }

  // returns tru if sucessfully deselecte
  private async deselectDependencies(optionItem: OptionApiModel, newOptionItem: OptionApiModel | undefined, forceDeselection = false) {
    if (optionItem.dependentOptions && optionItem.dependentOptions.length > 0) {
      const dependeciesToDeselect = this.getDependenciesToDeselect(optionItem, newOptionItem)

      if (dependeciesToDeselect.length > 0) {
        if (!forceDeselection && dependeciesToDeselect.some(d => !d.isHidden)) {
          const confirmed = await this.showConfirmDeselect(optionItem, dependeciesToDeselect.filter(d => !d.isHidden))
          if (!confirmed) { return false }
        }
        for (const option of dependeciesToDeselect) {
          await this.deselectOptionInternal(option, forceDeselection, true)
        }
      }
    }

    return true
  }

  private getDependenciesToDeselect(optionItem: OptionApiModel, newOptionItem: OptionApiModel | undefined,
    alreadySearched: Array<OptionApiModel> = []) {
    let optionDeps = this
      .optionStore
      .allOptions
      .filter(o => optionItem.dependentOptions?.includes(o.code))
      .filter(o => !alreadySearched.includes(o))
      .filter(o => this.isOptionSelected(o))
      .filter(o => this.dependencyNeedsDeselecting(o, optionItem, newOptionItem))

    alreadySearched.push(optionItem)

    for (const option of optionDeps) {
      const childDeps = this.getDependenciesToDeselect(option, newOptionItem, alreadySearched)
      optionDeps = optionDeps.concat(childDeps)
    }

    return optionDeps
  }

  private dependencyNeedsDeselecting(dependency: OptionApiModel, currentItem: OptionApiModel, newlySelectedItem?: OptionApiModel) {
    if (dependency.requiredOptionsLogic === RequiredOptionsLogic.All) { return true }

    // check if we're switching to another required option
    if (newlySelectedItem) {
      if (this.isRequired(dependency, newlySelectedItem)) { return false }
    }

    if (!this.isAnyOtherRequiredSelected(dependency, currentItem)) { return true }

    return false
  }

  private isRequired(optionItem: OptionApiModel, requiredOption: OptionApiModel) {
    const requiredOptions = this.optionStore.allOptions.filter(o => optionItem.requiredOptions?.includes(o.code))
    return requiredOptions.some(option => requiredOption === option)
  }

  private isAnyOtherRequiredSelected(optionItem: OptionApiModel, selectedRequiredOption: OptionApiModel) {
    const requiredOptions = this.optionStore.allOptions.filter(o => optionItem.requiredOptions?.includes(o.code))
    return requiredOptions.some(option => selectedRequiredOption !== option && this.isOptionSelected(option))
  }

  private showConfirmDeselect(optionItem: OptionApiModel, dependentOptions: OptionApiModel[]) {
    return new Promise(resolve => {
      const data = new ConfirmModalData(resolve)
      data.title = 'Remove options?'
      data.question = '<div class="mb-1">Following options depend on \'' + optionItem.name + '\':</div><ul class="mb-0">' +
        dependentOptions.map(o => '<li class="mb-1">' + o.name + '</li>').join('') + '</ul><div>and will be also removed.</div>'
      data.confirmButton = 'CONTINUE'
      this.$root.$emit('openConfirmModal', data)
    })
  }

  private selectSubOption(subOptionType: SubOptionType, subOptionItem: OptionApiModel) {
    this.updateModelProperty(
      {
        property: subOptionType.modelProperty,
        value: subOptionItem.code
      })
  }

  public isOptionSelected(optionItem: OptionApiModel) {
    return this.isOptionSelectedWithModel(optionItem, this.model)
  }

  public isOptionSelectedWithModel(optionItem: OptionApiModel, dataModel: ProductApiModel) {
    const modelProperty = optionItem.optionType.modelProperty
    if (optionItem.optionType.multipleSelection) {
      const optionArray = this.model.configurator[modelProperty] as Array<string>
      if (optionArray) {
        return optionArray.includes(optionItem.code)
      }
      return false
    }
    return optionItem.code === dataModel.configurator[modelProperty]
  }

  private updateSubOptions(category: OptionCategoryApiModel) {
    if (!category.subOptionTypes || category.subOptionTypes.length === 0) {
      return
    }

    category
      .subOptionTypes
      .forEach(type => {
        let subOption = type.subOptions.find(sub => sub.code === this.model.configurator[type.modelProperty] && !this.disableOption(sub))

        if (!subOption) {
          subOption = type.subOptions.find(sub => !this.disableOption(sub))
        }

        if (subOption) {
          this.selectSubOption(type, subOption)
        }
      })
  }
}
